import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  Button,
  TextField,
  Icon,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useStore } from 'app/store'
import CircularProgress from '@mui/material/CircularProgress'
import { debounce } from 'lodash'
import { ObjectType, User } from 'app/store/types'
import { getObjectTypeLabel } from 'app/helpers'
import { toast } from 'react-toastify'

const inputLabelProps = {
  classes: {
    shrink: 'text-black font-bold',
    focused: 'text-primary',
  },
}

type AutocompleteProps = React.ComponentProps<typeof Autocomplete>
interface CustomAutocompleteProps
  extends Omit<AutocompleteProps, 'options' | 'renderInput'> {
  label: string
  loadOptionsAsync: (value: any) => Promise<string[]>
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  label,
  loadOptionsAsync,
  ...rest
}) => {
  const [options, setOptions] = useState<string[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const loadOptions = async (searchValue: string) => {
    setLoadingOptions(true)
    try {
      setOptions([])
      const _options = await loadOptionsAsync(searchValue)
      setOptions(_options || [])
    } catch (error) {
    } finally {
      setLoadingOptions(false)
    }
  }

  const debounceLoadOptions = useCallback(debounce(loadOptions, 400), [])

  useEffect(() => {
    debounceLoadOptions(searchValue)
  }, [debounceLoadOptions, searchValue])

  const renderInput: AutocompleteProps['renderInput'] = (params) => {
    return (
      <TextField
        {...params}
        label={label}
        value={searchValue}
        onChange={(event) => {
          setSearchValue(event.target.value)
        }}
        InputLabelProps={inputLabelProps}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <React.Fragment>
              {loadingOptions ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
        variant="outlined"
        size="small"
        fullWidth
      />
    )
  }

  return (
    <Autocomplete
      multiple
      options={options}
      loading={loadingOptions}
      filterSelectedOptions
      renderInput={renderInput}
      {...rest}
    />
  )
}

type ShareObjectsDialogProps = {
  open: boolean
  onClose: () => void
  objects: { id: number; name: string; [key: string]: any }[]
  objectType: ObjectType
  withShareDetails?: boolean
  onSuccess?: () => void
  warningMessage?: string
  shareDetailsDefaultValue?: boolean
  addNewRequest: boolean
  onShareDetailsChanged?: (ids: number[], value: boolean) => void,
  renderExtra?: () => React.ReactNode,
  getExtraParams?: () => any,
}
const ShareObjectsDialog: React.FC<ShareObjectsDialogProps> = ({
  open,
  onClose,
  onSuccess,
  objects = [],
  objectType,
  withShareDetails,
  shareDetailsDefaultValue,
  warningMessage,
  addNewRequest = false,
  onShareDetailsChanged,
  renderExtra,
  getExtraParams,
}) => {
  const { sharing, usersStore } = useStore()
  const label = getObjectTypeLabel(objectType)

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<User[]>([])
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [shareDetails, setShareDetails] = useState(
    withShareDetails ? shareDetailsDefaultValue : false
  )
  const isSingleObject = objects.length === 1

  const loadData = async () => {
    setLoading(true)
    setErrored(false)
    try {
      const response = await sharing.getSharingObject(objectType, objects[0].id)
      setSelectedEmails(response?.userInfos || [])
      setSelectedGroups(response?.groups || [])
      setMessage(response?.message || '')
    } catch (error) {
      setErrored(true)
    } finally {
      setLoading(false)
    }
  }

  const loadEmailOptions = async (searchValue: string) => {
    return await usersStore
      .findUsersInCurrentUserGroupsWithName(searchValue)
      .then((response) => {
        return response;
      })
  }
  const loadGroupsOptions = async (searchValue: string) => {
    return await sharing
      .searchGroupsByName(searchValue)
      .then((response) => response.map((group: any) => group.groupName))
  }

  useEffect(() => {
    if (isSingleObject) {
      loadData()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSaveClick = async () => {
    if (saving) return
    setSaving(true)
    try {
      const ids = objects.map((object) => object.id)
      const extraParams = getExtraParams?.() || {}
      if (addNewRequest){
        await sharing.addShareObjectInf({
          objectType,
          objectIds: ids,
          emailIds: selectedEmails.map((user) => user.email),
          groups: selectedGroups,
          message,
          shareDetails: true,
          ...extraParams
        })

      }else{
        await sharing.shareObject({
          objectType,
          objectIds: ids,
          emailIds: selectedEmails.map((user) => user.email),
          groups: selectedGroups,
          message,
          shareDetails: true,
          ...extraParams
        })
      }

      if (withShareDetails && shareDetailsDefaultValue !== shareDetails) {
        await onShareDetailsChanged?.(ids, shareDetails!)
      }
      toast.success(
        isSingleObject
          ? `${label} ${objects[0].name} sharing status has been updated.`
          : 'Sharing status has been updated.'
      )
      onSuccess?.()
      onClose()
    } catch (error) {
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <h4 className="capitalize m-0 mb-4">Share {label}</h4>
      {loading ? (
        <CircularProgress />
      ) : errored ? (
        <span>Failed to load data</span>
      ) : (
        <>
          <CustomAutocomplete
            className="mb-4"
            label={'Emails'}
            value={selectedEmails}
            getOptionLabel={(option: any) =>
              [option.firstName, option.lastName].join(' ')
            }
            onChange={(_, value: any) => {
              setSelectedEmails(value as User[])}}
            loadOptionsAsync={loadEmailOptions}
            filterOptions={(options, { inputValue }) => {
              inputValue = inputValue?.toLowerCase();
              return options.filter((option: any) => {
                if(!inputValue) {
                  loadEmailOptions(inputValue)
                  return option
                }
              if(option?.firstName?.toLowerCase()?.indexOf(inputValue) > -1 || option?.lastname?.toLowerCase()?.indexOf(inputValue) > -1) {
                return option
              }
              })
            }}
            renderOption={(optionProps, option) => (
              <Box component="li" {...optionProps}>
                <div>
                  <div>
                    {/* @ts-ignore */}
                    <b>{[option.firstName, option.lastName || option.lastname].join(' ')}</b>
                  </div>
                  {/* @ts-ignore */}
                  <div className="text-xs text-gray-500">{option.email}</div>
                </div>
              </Box>
            )}
            disabled={saving}
          />
          <CustomAutocomplete
            label={'Groups'}
            value={selectedGroups}
            onChange={(_, value) => setSelectedGroups(value as string[])}
            loadOptionsAsync={loadGroupsOptions}
            disabled={saving}
          />
          <TextField
            className="mt-4"
            label={'Add a message'}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            maxRows={6}
            disabled={saving}
          />
          {withShareDetails && (
            <FormControlLabel
              className="mb-2"
              control={
                <Checkbox
                  color="primary"
                  checked={shareDetails}
                  onChange={(_, checked) => setShareDetails(checked)}
                  value="shareDetails"
                />
              }
              label="Share details"
            />
          )}
          {renderExtra?.()}

          {!!warningMessage && (
            <div className="flex my-2">
              <Icon className="text-warning mr-2"> warning_amber </Icon>
              <span className="pt-1">{warningMessage}</span>
            </div>
          )}
        </>
      )}

      <div className="flex justify-start pt-2 m--2">
        <Button
          className="m-2 rounded hover-bg-primary px-6"
          variant="outlined"
          color="primary"
          disabled={saving}
          onClick={errored ? loadData : onSaveClick}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {errored ? 'Reload' : 'Save'}
        </Button>
        <Button
          className="m-2 rounded hover-bg-error px-6"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Dialog>
  )
}

export default ShareObjectsDialog